import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { SelectableBox } from '@/components/ui/selectable-box'
import { Switch } from '@/components/ui/switch'
import { AnimatedSparkles } from '@/components/ui/animated-sparkles'
import { cn } from '@/lib/utils'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormChangeTracking,
} from '@/components/ui/form'
import { Card, CardContent } from '@/components/ui/card'
import {
  agentsApiClient,
  type AgentWithVoiceSettings,
} from '@/lib/api/agents.client'
import { agentsAdminApiClient } from '@/lib/api/agents-admin.client'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  AGENT_TONE_VALUES,
  VOICE_SETTINGS_DEFAULTS,
  VOICE_OPTIONS,
  type AgentTone,
} from '@super/shared-types'
const toneOptions = AGENT_TONE_VALUES

const llmOptions = [
  {
    id: 'gpt-4o-mini',
    label: 'GPT-4o Mini',
    description: 'OpenAI - fastest',
  },
  {
    id: 'gpt-4-turbo',
    label: 'GPT-4 Turbo',
    description: 'OpenAI - balanced',
  },
  {
    id: 'gpt-4o',
    label: 'GPT-4o',
    description: 'OpenAI - most capable',
  },
]

const sttOptions = [
  {
    id: 'nova-3',
    label: 'Nova',
    description: 'Deepgram',
  },
]

const voiceOptions = VOICE_OPTIONS

const formSchema = z.object({
  name: z.string().min(1, 'Agent name is required'),
  disallowedTopics: z.string().optional(),
  tone: z.array(z.enum(AGENT_TONE_VALUES)).min(1, 'Select at least one tone'),
  temperature: z.number().min(0).max(1),
  greeting: z.string().optional(),
  prompt: z.string().optional(),
  voice: z.string().min(1, 'Please select a voice'),
  voiceModelId: z.string().min(1, 'Voice model ID is required'),
  voiceModelLLM: z.string().min(1, 'Select an LLM model'),
  voiceModelSTT: z.string().min(1, 'Select an STT model'),
  transferInstructions: z.string().optional(),
  aiTransfersEnabled: z.boolean().optional(),
  declinedMessage: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

const getTemperatureLabel = (value: number): string => {
  if (value === 0) return 'Precise'
  if (value === 0.25) return 'Consistent'
  if (value === 0.5) return 'Balanced'
  if (value === 0.75) return 'Creative'
  if (value === 1) return 'Experimental'
  return `${value}`
}

export function AgentsVoicePage() {
  useDocumentHead({
    title: 'Voice Settings',
    description: 'Configure your voice agent settings',
  })

  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [agent, setAgent] = useState<AgentWithVoiceSettings | null>(null)
  const [formData, setFormData] = useState<FormValues | null>(null)
  const declinedMessageInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: VOICE_SETTINGS_DEFAULTS,
  })

  useEffect(() => {
    if (currentCompany?.id) {
      loadAgent()
    }
  }, [currentCompany?.id])

  // Watch for formData changes and reset form
  useEffect(() => {
    if (formData) {
      form.reset(formData)
    }
  }, [formData, form])

  const loadAgent = async () => {
    if (!currentCompany?.id) {
      console.error('No current company available')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      // Get all agents and find the earliest one (oldest by createdAt)
      // This ensures backward compatibility - the original agent is used
      const agents = isSuperAdmin
        ? await agentsAdminApiClient.getAgentsForCompany(currentCompany.id)
        : await agentsApiClient.getCompanyAgents()

      // Sort by createdAt ascending and get the first (oldest) agent
      const sortedAgents = [...agents].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      const existingAgent = sortedAgents.length > 0 ? sortedAgents[0] : null

      if (existingAgent) {
        setAgent(existingAgent)

        // Find matching voice or fallback to first option
        const existingVoiceModelId =
          existingAgent.voiceSettings?.voiceModelId ?? ''
        const matchingVoice = VOICE_OPTIONS.find(
          opt => opt.id === existingVoiceModelId,
        )
        const selectedVoice = matchingVoice ?? VOICE_OPTIONS[0]

        // Find matching STT model or fallback to first available option
        const existingSTTModelId =
          existingAgent.voiceSettings?.voiceModelSTT ?? ''
        const matchingSTT = sttOptions.find(
          opt => opt.id === existingSTTModelId,
        )
        const selectedSTT = matchingSTT ?? sttOptions[0]

        const newFormData = {
          name: existingAgent.name,
          disallowedTopics:
            existingAgent.voiceSettings?.disallowedTopics ||
            VOICE_SETTINGS_DEFAULTS.disallowedTopics,
          tone:
            existingAgent.voiceSettings?.tone || VOICE_SETTINGS_DEFAULTS.tone,
          temperature:
            existingAgent.voiceSettings?.temperature ||
            VOICE_SETTINGS_DEFAULTS.temperature,
          greeting:
            existingAgent.voiceSettings?.greeting ||
            VOICE_SETTINGS_DEFAULTS.greeting,
          voice: selectedVoice.label,
          voiceModelId: selectedVoice.id,
          voiceModelLLM:
            existingAgent.voiceSettings?.voiceModelLLM ||
            VOICE_SETTINGS_DEFAULTS.voiceModelLLM,
          voiceModelSTT: selectedSTT.id,
          transferInstructions:
            existingAgent.voiceSettings?.transferInstructions || '',
          aiTransfersEnabled:
            existingAgent.voiceSettings?.aiTransfersEnabled ??
            VOICE_SETTINGS_DEFAULTS.aiTransfersEnabled,
          declinedMessage:
            existingAgent.voiceSettings?.declinedMessage ||
            VOICE_SETTINGS_DEFAULTS.declinedMessage,
        }
        setFormData(newFormData)
        form.reset(newFormData)
      } else {
        // No agent exists, use defaults
        setFormData(VOICE_SETTINGS_DEFAULTS)
        form.reset(VOICE_SETTINGS_DEFAULTS)
      }
    } catch (error) {
      console.error('Error loading agent:', error)
      toast.error('Failed to load agent settings')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No current company selected')
      return false
    }

    try {
      setSaving(true)

      const requestData = {
        name: values.name,
        direction: 'INBOUND' as const,
        voiceSettings: {
          disallowedTopics: values.disallowedTopics || undefined,
          tone: values.tone,
          greeting: values.greeting || undefined,
          transferInstructions: values.transferInstructions || undefined,
          aiTransfersEnabled: values.aiTransfersEnabled ?? false,
          declinedMessage: values.declinedMessage || undefined,
        },
      }

      if (agent) {
        // Update existing agent
        const updated = isSuperAdmin
          ? await agentsAdminApiClient.updateAgentForCompany(
              agent.id,
              requestData,
            )
          : await agentsApiClient.updateAgent(agent.id, requestData)
        setAgent(updated)
        toast.success('Agent settings updated successfully')
      } else {
        // Create new agent
        const created = isSuperAdmin
          ? await agentsAdminApiClient.createAgentForCompany({
              ...requestData,
              companyId: currentCompany.id,
            })
          : await agentsApiClient.createAgent(requestData)
        setAgent(created)
        toast.success('Agent created successfully')
      }
      return true // Success
    } catch (error) {
      console.error('Error saving agent:', error)
      toast.error('Failed to save agent settings')
      return false // Failure
    } finally {
      setSaving(false)
    }
  }

  // Component to handle floating island that must be inside Form provider
  const FloatingIslandContent = () => {
    const { hasUnsavedChanges, changedFieldsCount } = useFormChangeTracking()

    const handleSubmit = async (values: FormValues) => {
      const success = await onSubmit(values)
      if (success) {
        // Reset form to mark as not dirty (hide floating island)
        form.reset(values)
      }
    }

    return (
      <FloatingActionIslandContent
        show={hasUnsavedChanges}
        componentId="agent-settings-save"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center mr-4 space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none">
            <span className="font-medium">
              <span className="mr-1">
                {changedFieldsCount} <span>unsaved&nbsp;</span>
                {changedFieldsCount === 1 ? 'change' : 'changes'}
              </span>
            </span>
          </div>

          <div className="flex justify-end items-center space-x-2 pointer-events-auto">
            <Button
              onClick={form.handleSubmit(handleSubmit)}
              disabled={saving}
              size="sm"
              variant="form"
            >
              <span>
                {saving ? 'Saving...' : agent ? 'Update agent' : 'Create agent'}
              </span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    )
  }

  // Show skeleton during loading or when form data is not ready
  if (loading || !formData) {
    return (
      <div className="p-6 pt-16 space-y-6 mt-6">
        <PageHeader title="Configure your voice agent settings" />

        <Card>
          <CardContent className="pt-6 max-w-156">
            <div className="space-y-6">
              <div className="min-w-56">
                <Skeleton className="mb-2 w-24 h-4" />
                <Skeleton className="w-full h-10 max-w-96" />
              </div>
              <div className="min-w-56">
                <Skeleton className="mb-2 w-32 h-4" />
                <Skeleton className="w-full h-32" />
              </div>
              <div>
                <Skeleton className="mb-2 w-16 h-4" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="w-20 h-10" />
                  ))}
                </div>
              </div>
              <div className="min-w-56">
                <Skeleton className="mb-2 w-28 h-4" />
                <Skeleton className="w-full h-24" />
              </div>
              <div className="min-w-56">
                <Skeleton className="mb-2 w-24 h-4" />
                <Skeleton className="w-full h-8 max-w-96" />
              </div>
              <div className="min-w-56">
                <Skeleton className="mb-2 w-28 h-4" />
                <Skeleton className="w-full h-10" />
              </div>
              <div>
                <Skeleton className="mb-2 w-36 h-4" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="w-32 h-20" />
                  ))}
                </div>
              </div>
              <div>
                <Skeleton className="mb-2 w-40 h-4" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <Skeleton key={i} className="w-32 h-20" />
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="pt-16 h-full">
      <FloatingActionIslandProvider>
        <div className="p-6 pt-0 pb-24 space-y-6 mt-6">
          <PageHeader title="Configure your voice agent settings" />

          <Card>
            <CardContent className="pt-6 max-w-156">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <div className="min-w-56">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="mb-8 max-w-96">
                          <FormLabel className="mb-2">Agent name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter the name of the voice agent"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="min-w-56">
                    <FormField
                      control={form.control}
                      name="disallowedTopics"
                      render={({ field }) => (
                        <FormItem className="mb-8">
                          <FormLabel className="mb-2">
                            Off-limit topics
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Enter topics the agent should avoid discussing"
                              className="min-h-[120px]"
                              rows={4}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="tone"
                    render={({ field }) => (
                      <FormItem className="mb-8">
                        <FormLabel className="mb-2">Tone</FormLabel>
                        <FormControl>
                          <div className="flex flex-wrap gap-2">
                            {toneOptions.map(tone => (
                              <SelectableBox
                                key={tone}
                                id={tone}
                                selected={field.value.includes(tone)}
                                onSelect={id => {
                                  const typedId = id as AgentTone
                                  const newValue = field.value.includes(typedId)
                                    ? field.value.filter(t => t !== typedId)
                                    : [...field.value, typedId]
                                  field.onChange(newValue)
                                }}
                              >
                                <span className="capitalize">{tone}</span>
                              </SelectableBox>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="min-w-56">
                    <FormField
                      control={form.control}
                      name="greeting"
                      render={({ field }) => (
                        <FormItem className="mb-8">
                          <FormLabel className="mb-2">Voice greeting</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Hello! How can I help you today?"
                              className="min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="prompt"
                      render={({ field }) => (
                        <FormItem className="mb-8">
                          <FormLabel className="mb-2">Prompt</FormLabel>
                          <p className="text-sm text-muted-foreground mb-2">
                            Detailed instructions for how the agent should
                            behave
                          </p>
                          <FormControl>
                            <Textarea
                              placeholder="You are a helpful assistant..."
                              className="min-h-[200px] text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="voiceModelId"
                    render={({ field }) => (
                      <FormItem className="mb-8">
                        <FormLabel className="mb-2">Voice Selection</FormLabel>
                        <FormControl>
                          <div className="flex flex-wrap gap-2">
                            {voiceOptions.map(option => (
                              <SelectableBox
                                key={option.id}
                                id={option.id}
                                selected={field.value === option.id}
                                onSelect={id => {
                                  field.onChange(id)
                                  const selectedOption = voiceOptions.find(
                                    o => o.id === id,
                                  )
                                  form.setValue(
                                    'voice',
                                    selectedOption?.label || '',
                                  )
                                }}
                              >
                                <div className="space-y-0.5">
                                  <div className="font-medium">
                                    {option.label}
                                  </div>
                                  <div
                                    className={cn(
                                      'text-xs',
                                      field.value === option.id
                                        ? 'text-white/80'
                                        : 'text-[#757575]',
                                    )}
                                  >
                                    {option.description}
                                  </div>
                                </div>
                              </SelectableBox>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="min-w-56">
                    <FormField
                      control={form.control}
                      name="temperature"
                      render={({ field }) => (
                        <FormItem className="mb-8 max-w-96">
                          <FormLabel className="mb-2">Temperature</FormLabel>
                          <FormControl>
                            <Slider
                              min={0}
                              max={1}
                              step={0.25}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              getTooltipLabel={getTemperatureLabel}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="voiceModelLLM"
                    render={({ field }) => (
                      <FormItem className="mb-8">
                        <FormLabel className="mb-2">
                          Language Model (LLM)
                        </FormLabel>
                        <FormControl>
                          <div className="flex flex-wrap gap-2">
                            {llmOptions.map(option => (
                              <SelectableBox
                                key={option.id}
                                id={option.id}
                                selected={field.value === option.id}
                                onSelect={id => field.onChange(id)}
                              >
                                <div className="space-y-0.5">
                                  <div className="font-medium">
                                    {option.label}
                                  </div>
                                  <div
                                    className={cn(
                                      'text-xs',
                                      field.value === option.id
                                        ? 'text-white/80'
                                        : 'text-[#757575]',
                                    )}
                                  >
                                    {option.description}
                                  </div>
                                </div>
                              </SelectableBox>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="voiceModelSTT"
                    render={({ field }) => (
                      <FormItem className="mb-8">
                        <FormLabel className="mb-2">
                          Speech-to-Text Model (STT)
                        </FormLabel>
                        <FormControl>
                          <div className="flex flex-wrap gap-2">
                            {sttOptions.map(option => (
                              <SelectableBox
                                key={option.id}
                                id={option.id}
                                selected={field.value === option.id}
                                onSelect={id => field.onChange(id)}
                              >
                                <div className="space-y-0.5">
                                  <div className="font-medium">
                                    {option.label}
                                  </div>
                                  <div
                                    className={cn(
                                      'text-xs',
                                      field.value === option.id
                                        ? 'text-white/80'
                                        : 'text-[#757575]',
                                    )}
                                  >
                                    {option.description}
                                  </div>
                                </div>
                              </SelectableBox>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="min-w-56">
                    <FormField
                      control={form.control}
                      name="transferInstructions"
                      render={({ field }) => (
                        <FormItem className="mb-8">
                          <FormLabel className="mb-2">
                            Transfer instructions
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Enter instructions for how and when to transfer calls"
                              rows={12}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="aiTransfersEnabled"
                    render={({ field }) => (
                      <FormItem className="mb-8">
                        <div className="flex items-center max-w-96">
                          <FormLabel className="mb-0 flex items-center gap-2">
                            <AnimatedSparkles
                              className="w-4 h-4"
                              animate={field.value ?? false}
                            />
                            AI transfers
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value ?? false}
                              onCheckedChange={checked => {
                                field.onChange(checked)
                                if (checked) {
                                  // Wait for the disabled state to update, then focus
                                  requestAnimationFrame(() => {
                                    declinedMessageInputRef.current?.focus()
                                  })
                                }
                              }}
                              className={cn(
                                'transition-all duration-300',
                                field.value &&
                                  'bg-gradient-to-r from-violet-800 to-purple-600 animate-glow',
                              )}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="min-w-56">
                    <FormField
                      control={form.control}
                      name="declinedMessage"
                      render={({ field }) => {
                        const { ref: fieldRef, ...fieldProps } = field
                        return (
                          <FormItem className="mb-8">
                            <FormLabel className="mb-2">
                              Agent message when transfer is declined
                            </FormLabel>
                            <FormControl>
                              <Input
                                id="declinedMessage"
                                placeholder="The team member isn't available. Can I leave a message?"
                                disabled={!form.watch('aiTransfersEnabled')}
                                ref={e => {
                                  fieldRef(e)
                                  declinedMessageInputRef.current = e
                                }}
                                {...fieldProps}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                  </div>
                </form>

                <FloatingIslandContent />
              </Form>
            </CardContent>
          </Card>
        </div>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
